import {
  clsx_default
} from "../../../chunk-QJVDQ4DQ.mjs";
import "../../../chunk-D57E6H3M.mjs";

// src/components/composite/ChatMessage/ChatMessage.tsx
import * as React from "react";
import { useRef, useState } from "react";
import { MdOutlineThumbDown, MdOutlineThumbUp, MdThumbDown, MdThumbUp } from "react-icons/md";
import { ChatRole, ChatStreamStatus, isStreamLive, shouldRenderChatMessage } from "../../../domain/index.mjs";
import { useExitPresence } from "../../../hooks/useExitPresence.mjs";
import { useShadowPortalContainer } from "../../../hooks/useShadowPortalContainer.mjs";
import { useSlideToFit } from "../../../hooks/useSlideToFit.mjs";
import { useComponentsTranslation } from "../../../i18n/useComponentsTranslation.mjs";
import { Button, CopyButton, Spinner, Text, Tooltip } from "../../primitive/index.mjs";
import { ChatMarkdown } from "../ChatMarkdown/index.mjs";
import { TimelineRenderer } from "../Timeline/index.mjs";
import { DislikeReasonPopover } from "./DislikeReasonPopover.mjs";
import { getMessageCopyText } from "./messageCopyText.mjs";
import { showsOwnProgress, useStreamStall } from "./useStreamStall.mjs";
import { jsx, jsxs } from "react/jsx-runtime";
function ChatMessageImpl({
  message,
  isUser,
  showFadeAnimation = true,
  style = void 0,
  className = void 0,
  sessionStatus,
  isLastMessage = true,
  isLastInAssistantRun = true,
  trailingContent,
  footer,
  pending = false,
  productMap,
  storeDomain,
  showCopyButton = false,
  onAssistantMessageLike,
  onAssistantMessageDislike,
  onAssistantMessageDislikeReason,
  onAddToCart,
  onClarifyingQuestionsComplete,
  onInlineFormSubmit,
  onInlineFormDismiss,
  dashboardMode = false,
  onToolClick,
  timelineRenderers
}) {
  const hasFooter = !isUser && footer != null;
  if (!shouldRenderChatMessage(message) && !hasFooter) return null;
  const canCopy = showCopyButton && getMessageCopyText(message).trim() !== "";
  const willShowAssistantToolbar = !isUser && isLastInAssistantRun && !hasFooter && (canCopy || onAssistantMessageLike != null || onAssistantMessageDislike != null);
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: clsx_default(
        "buyscout-chat-message-container",
        isUser ? "user" : "assistant",
        !isUser && !willShowAssistantToolbar && "buyscout-chat-message-container--no-assistant-toolbar",
        className
      ),
      style,
      "data-message-id": message.id,
      children: /* @__PURE__ */ jsx("div", { className: clsx_default("buyscout-chat-message-content-wrapper", showFadeAnimation && "buyscout-chat-message-content-wrapper--animated"), children: /* @__PURE__ */ jsx(
        MessageContent,
        {
          message,
          sessionStatus,
          isLastMessage,
          isLastInAssistantRun,
          trailingContent,
          footer,
          pending,
          productMap,
          storeDomain,
          showCopyButton: canCopy,
          onAssistantMessageLike,
          onAssistantMessageDislike,
          onAssistantMessageDislikeReason,
          onAddToCart,
          onClarifyingQuestionsComplete,
          onInlineFormSubmit,
          onInlineFormDismiss,
          dashboardMode,
          onToolClick,
          timelineRenderers
        }
      ) })
    }
  );
}
var ChatMessage = React.memo(ChatMessageImpl);
ChatMessage.displayName = "ChatMessage";
function MessageAction({ title, ariaLabel, icon: Icon, pressedIcon: PressedIcon, onClick, pressed, buttonRef, beatOnPress = false }) {
  const [beating, setBeating] = useState(false);
  const handleClick = () => {
    if (beatOnPress && !pressed) setBeating(true);
    onClick();
  };
  return /* @__PURE__ */ jsx(Tooltip, { title, children: /* @__PURE__ */ jsx(Button, { ref: buttonRef, variant: "borderless", color: "neutral", size: "xs", iconOnly: true, "aria-label": ariaLabel, "aria-pressed": pressed, onClick: handleClick, children: PressedIcon ? /* @__PURE__ */ jsxs("span", { className: "buyscout-chat-message-action-glyphs", "aria-hidden": true, "data-beat": beating || void 0, onAnimationEnd: () => setBeating(false), children: [
    /* @__PURE__ */ jsx(Icon, { "data-shown": !pressed || void 0 }),
    /* @__PURE__ */ jsx(PressedIcon, { "data-shown": pressed || void 0 })
  ] }) : /* @__PURE__ */ jsx(Icon, { "aria-hidden": true }) }) });
}
function MessageContent({
  message,
  sessionStatus,
  isLastMessage = true,
  isLastInAssistantRun = true,
  trailingContent,
  footer,
  pending = false,
  productMap,
  storeDomain,
  showCopyButton = false,
  onAssistantMessageLike,
  onAssistantMessageDislike,
  onAssistantMessageDislikeReason,
  onAddToCart,
  onClarifyingQuestionsComplete,
  onInlineFormSubmit,
  onInlineFormDismiss,
  dashboardMode = false,
  onToolClick,
  timelineRenderers
}) {
  const { t } = useComponentsTranslation();
  const animateState = useRef({ charStartTimes: /* @__PURE__ */ new Map() });
  const [askDislikeReason, setAskDislikeReason] = useState(false);
  const dislikeButtonRef = useRef(null);
  const [reasonContainer, attachDislikeButton] = useShadowPortalContainer(dislikeButtonRef);
  const isInProgress = isStreamLive(sessionStatus);
  const isStreaming = message.role === ChatRole.User ? Boolean(message.speaking) : Boolean(isLastMessage && isInProgress && message.role === ChatRole.Assistant);
  if (!isStreaming) {
    animateState.current.charStartTimes.clear();
  }
  const replyTimeline = message.role === ChatRole.Assistant ? message.timeline : void 0;
  const lastShownItem = replyTimeline?.filter((item) => item.type !== "possible_replies").at(-1);
  const stalled = useStreamStall(replyTimeline, isStreaming && message.role === ChatRole.Assistant) && !showsOwnProgress(lastShownItem);
  const stallRef = useRef(null);
  const { rendered: showStall, closing: stallClosing } = useExitPresence(stalled || void 0, stallRef);
  const userBubbleRef = useRef(null);
  useSlideToFit(userBubbleRef, message.role === ChatRole.User ? message.content : void 0, message.role === ChatRole.User && isStreaming);
  const copyAction = /* @__PURE__ */ jsx(CopyButton, { value: getMessageCopyText(message), variant: "borderless", size: "xs", "aria-label": t("chatMessage.copyAria") });
  if (message.role === ChatRole.User) {
    return /* @__PURE__ */ jsxs("div", { className: "buyscout-chat-message-user-wrapper", children: [
      /* @__PURE__ */ jsx(
        "div",
        {
          ref: userBubbleRef,
          "data-testid": "chat-message",
          "data-role": "user",
          className: clsx_default(
            "buyscout-chat-message-bubble",
            "user",
            pending && "is-pending"
          ),
          children: /* @__PURE__ */ jsx(Text, { variant: "body", as: "div", className: "buyscout-message-content user", children: /* @__PURE__ */ jsx(ChatMarkdown, { content: message.content, productMap, storeDomain, isStreaming, animateState: animateState.current }) })
        }
      ),
      showCopyButton && /* @__PURE__ */ jsx("div", { className: "buyscout-chat-message-user-actions", role: "group", "aria-label": t("chatMessage.actionsAria"), children: copyAction })
    ] });
  }
  const feedback = message.feedback;
  const handleAssistantLike = () => {
    setAskDislikeReason(false);
    onAssistantMessageLike?.(message);
  };
  const handleAssistantDislike = () => {
    setAskDislikeReason(feedback !== "dislike");
    onAssistantMessageDislike?.(message);
  };
  const showCopy = showCopyButton;
  const showLike = onAssistantMessageLike != null;
  const showDislike = onAssistantMessageDislike != null;
  const hasAnyAssistantAction = isLastInAssistantRun && (showCopy || showLike || showDislike);
  const actionsPending = isLastMessage && sessionStatus === ChatStreamStatus.InProgress;
  const askingDislikeReason = hasAnyAssistantAction && showDislike && onAssistantMessageDislikeReason != null && askDislikeReason && feedback === "dislike";
  const assistantActions = hasAnyAssistantAction && /* @__PURE__ */ jsxs(
    "div",
    {
      className: "buyscout-chat-message-assistant-actions",
      "data-holding": askingDislikeReason || void 0,
      role: "group",
      "aria-label": t("chatMessage.actionsAria"),
      inert: actionsPending,
      "data-pending": actionsPending || void 0,
      children: [
        showCopy && copyAction,
        showLike && /* @__PURE__ */ jsx(MessageAction, { title: t("chatMessage.goodResponseTitle"), ariaLabel: t("chatMessage.likeAria"), icon: MdOutlineThumbUp, pressedIcon: MdThumbUp, onClick: handleAssistantLike, pressed: feedback === "like", beatOnPress: true }),
        showDislike && /* @__PURE__ */ jsx(MessageAction, { title: t("chatMessage.badResponseTitle"), ariaLabel: t("chatMessage.dislikeAria"), icon: MdOutlineThumbDown, pressedIcon: MdThumbDown, onClick: handleAssistantDislike, pressed: feedback === "dislike", buttonRef: attachDislikeButton })
      ]
    }
  );
  const dislikeReasonPopover = askingDislikeReason && /* @__PURE__ */ jsx(
    DislikeReasonPopover,
    {
      message,
      anchorRef: dislikeButtonRef,
      container: reasonContainer,
      onPick: onAssistantMessageDislikeReason,
      onClose: () => setAskDislikeReason(false)
    }
  );
  if (footer != null && (message.timeline?.length ?? 0) === 0) {
    return /* @__PURE__ */ jsx("div", { className: "buyscout-chat-message-assistant-wrapper", children: footer });
  }
  if (message.timeline !== void 0 && message.timeline.length > 0) {
    const timelineHasContentItem = message.timeline.some((item) => item.type === "content");
    const legacyContent = message.content;
    const effectiveTimeline = !timelineHasContentItem && legacyContent?.trim() ? [...message.timeline, { type: "content", content: legacyContent, timestamp: 0 }] : message.timeline;
    return /* @__PURE__ */ jsxs("div", { className: "buyscout-chat-message-assistant-wrapper", children: [
      /* @__PURE__ */ jsx("div", { className: "buyscout-chat-message-timeline", children: /* @__PURE__ */ jsx(
        TimelineRenderer,
        {
          timeline: effectiveTimeline,
          sessionStatus,
          productMap,
          storeDomain,
          onAddToCart,
          onClarifyingQuestionsComplete,
          onInlineFormSubmit,
          onInlineFormDismiss,
          dashboardMode,
          clarifyingSuperseded: !isLastMessage,
          inlineFormSuperseded: !isLastMessage,
          onToolClick,
          renderers: timelineRenderers
        }
      ) }),
      showStall && /* @__PURE__ */ jsx("div", { ref: stallRef, className: "buyscout-chat-message-stall", "data-state": stallClosing ? "closed" : "open", "aria-hidden": true, children: /* @__PURE__ */ jsx(Spinner, { size: "sm" }) }),
      trailingContent != null && /* @__PURE__ */ jsx("div", { className: "buyscout-chat-message-assistant-trailing-content", children: trailingContent }),
      footer != null ? /* @__PURE__ */ jsx("div", { className: "buyscout-chat-message-assistant-footer", children: footer }) : assistantActions,
      footer == null && dislikeReasonPopover
    ] });
  }
  return null;
}
export {
  ChatMessage
};
//# sourceMappingURL=ChatMessage.mjs.map